import type { SphereConfig } from "@dyson/shared";

/** Who we are to the game, and where to connect. */
export interface Session {
  /** Sent in "join" and "view": says who we are to whichever area we connect to. */
  ticket: string;
  /** The region our character is in (its key: "x,y" for an area, "s:i:k" for a segment of the stream; null: the sphere is one area). */
  region: string | null;
  sphere: SphereConfig;
  /** Logins (massivewebgames.com) are available: show "Save your character". */
  accounts: boolean;
  /** Signed in (not a guest), and as whom. */
  user?: { id: string; name: string };
}

/** Node's guests keep the token their character came with; the Worker's keep a guest token it signed. */
const NODE_TOKEN_KEY = "dyson.token";
const GUEST_KEY = "dyson.guest";
/** The shared password a server may ask for before anyone plays (staging). */
const PASSWORD_KEY = "dyson.password";

/** The server asked for its shared password (and the one we had, if any, was wrong). */
export class PasswordRequired extends Error {
  constructor() {
    super("This server needs a password to play.");
  }
}

/** Remember the shared password, to send with every session request. */
export function setAccessPassword(password: string): void {
  localStorage.setItem(PASSWORD_KEY, password);
}

/** The password header, if we have one. */
function passwordHeader(): Record<string, string> {
  const password = localStorage.getItem(PASSWORD_KEY);
  return password ? { "x-access-password": password } : {};
}

/** A 401 that says the password is missing or wrong: forget the one we had, and say so. */
async function checkPassword(res: Response): Promise<void> {
  if (res.status !== 401) return;
  const body = (await res.clone().json().catch(() => ({}))) as { error?: string };
  if (body.error !== "password_required") return;
  localStorage.removeItem(PASSWORD_KEY);
  throw new PasswordRequired();
}

/** The access token while signed in, kept in memory only (see plan.md, "Tokens"). */
let accessToken: { token: string; user: { id: string; name: string }; until: number } | undefined;

/**
 * Start (or renew) a session with the server at `base` (http(s) URL):
 * signed in if the refresh cookie still works, else as a guest (made on the
 * first visit), then a ticket and our area from POST /api/session.
 */
export async function startSession(base: string): Promise<Session> {
  const bearer = (await signedIn(base)) ?? (await guestToken(base));
  const res = await fetch(`${base}/api/session`, {
    method: "POST",
    headers: { "content-type": "application/json", ...passwordHeader(), ...(bearer ? { authorization: `Bearer ${bearer}` } : {}) },
    body: JSON.stringify({ token: localStorage.getItem(NODE_TOKEN_KEY) ?? undefined }),
  });
  await checkPassword(res);
  if (!res.ok) throw new Error(res.status === 401 ? "Your session has expired: reload the page." : `The server said ${res.status}.`);
  const s = (await res.json()) as Omit<Session, "user">;
  return { ...s, user: accessToken?.user };
}

/** The Node server's guests: it hands out a token in "welcome" to keep. */
export function keepNodeToken(token: string): void {
  if (token) localStorage.setItem(NODE_TOKEN_KEY, token);
}

/** An access token, refreshed with the refresh cookie when it's near its end; undefined when not signed in. */
async function signedIn(base: string): Promise<string | undefined> {
  if (accessToken && accessToken.until > Date.now() + 60_000) return accessToken.token;
  try {
    const res = await fetch(`${base}/auth/refresh`, { method: "POST", credentials: "include" });
    if (!res.ok) return (accessToken = undefined);
    const body = (await res.json()) as { accessToken: string; expiresIn: number; user: { id: string; name: string } };
    accessToken = { token: body.accessToken, user: body.user, until: Date.now() + body.expiresIn * 1000 };
    return accessToken.token;
  } catch {
    // No accounts here (the Node server), or offline.
    return undefined;
  }
}

/** Our guest token, made on the first visit (none on the Node server, which uses its own token). */
async function guestToken(base: string): Promise<string | undefined> {
  const saved = localStorage.getItem(GUEST_KEY);
  if (saved) return saved;
  let res: Response;
  try {
    res = await fetch(`${base}/api/guest`, { method: "POST", headers: passwordHeader() });
  } catch {
    return undefined;
  }
  await checkPassword(res);
  try {
    if (!res.ok) return undefined;
    const { guestToken } = (await res.json()) as { guestToken: string };
    localStorage.setItem(GUEST_KEY, guestToken);
    return guestToken;
  } catch {
    return undefined;
  }
}

/**
 * "Save your character": off to massivewebgames.com to sign up or log in,
 * then back here, where the game makes this guest's character the account's.
 */
export async function saveCharacter(base: string): Promise<void> {
  const res = await fetch(`${base}/auth/start`, {
    method: "POST",
    credentials: "include",
    headers: { authorization: `Bearer ${localStorage.getItem(GUEST_KEY) ?? ""}`, ...passwordHeader() },
  });
  await checkPassword(res);
  if (!res.ok) throw new Error(`Couldn't start signing in (${res.status}).`);
  const { url } = (await res.json()) as { url: string };
  location.href = url;
}

/** Sign out: the refresh cookie is revoked; next time we're a guest again (a new one: the old guest became the account). */
export async function signOut(base: string): Promise<void> {
  await fetch(`${base}/auth/logout`, { method: "POST", credentials: "include" });
  accessToken = undefined;
  localStorage.removeItem(GUEST_KEY);
}
